import { CSSProperties, PropsWithChildren, RefObject, useEffect, useRef } from "react";
import React from "react";
import { Menu, MenuProps, PopoverActions, SxProps, Theme, useTheme } from "@mui/material";
import { CONTEXT_MENU_LIST_TEST_ID } from "src/components-mui/base/context-menu/context-menu.test-ids";
import { useNonBlockingMenu } from "src/components-mui/base/context-menu/use-non-blocking-menu";
import { toSxArray } from "src/utils/mui/sx/sx";

export interface IContextMenuProps extends Omit<MenuProps, "open" | "anchorEl"> {
    open: boolean;
    anchorRef: RefObject<HTMLElement>;
    sx?: SxProps<Theme>;
    anchorGap?: number;
    zIndex?: number;
    /**
     * Keeps the rest of the page interactive while the menu is open instead of
     * blocking it behind the modal backdrop. Defaults to `true`.
     */
    keepPageInteractive?: boolean;
}

/** Narrowed view of the slot props this component reads from and overrides. */
interface IMenuSlots {
    root?: { sx?: SxProps<Theme> };
    backdrop?: { style?: CSSProperties };
    paper?: { sx?: SxProps<Theme> };
    list?: Record<string, unknown>;
}

const getMergedSx = (sx: SxProps<Theme> | undefined, anchorGap: number, zIndex?: number): SxProps<Theme> => {
    const anchorGapSx = { "& .MuiMenu-paper": { marginTop: anchorGap, fontSize: "16px" } };
    const zIndexSx = zIndex === undefined ? {} : { zIndex };

    return [...toSxArray(sx), anchorGapSx, zIndexSx] as SxProps<Theme>;
};

const getDefaultSlotProps = (slotProps: MenuProps["slotProps"]): MenuProps["slotProps"] => {
    const slots = (slotProps ?? {}) as IMenuSlots;

    return {
        backdrop: { style: { backgroundColor: "transparent" } },
        ...slotProps,
        list: { "data-test-id": CONTEXT_MENU_LIST_TEST_ID, ...slots.list },
    } as unknown as MenuProps["slotProps"];
};

/**
 * Makes the modal layer click-through (page stays interactive) while keeping the
 * menu surface itself interactive. The paper ref lets the menu detect outside
 * clicks since the now click-through backdrop no longer fires `onClose`.
 *
 * Sets aria-hidden={false} to prevent a11y violations when focusing menu items
 * inside a container that would normally have aria-hidden="true".
 */
const getNonBlockingSlotProps = (slotProps: MenuProps["slotProps"], paperRef: RefObject<HTMLDivElement>): MenuProps["slotProps"] => {
    const slots = (slotProps ?? {}) as IMenuSlots;

    return {
        ...slotProps,
        list: { "data-test-id": CONTEXT_MENU_LIST_TEST_ID, ...slots.list },
        root: {
            ...slots.root,
            "aria-hidden": false,
            sx: [{ pointerEvents: "none" }, ...toSxArray(slots.root?.sx)],
        },
        backdrop: {
            ...slots.backdrop,
            style: { backgroundColor: "transparent", pointerEvents: "none", ...slots.backdrop?.style },
        },
        paper: {
            ...slots.paper,
            ref: paperRef,
            sx: [{ pointerEvents: "auto" }, ...toSxArray(slots.paper?.sx)],
        },
    } as unknown as MenuProps["slotProps"];
};

export const ContextMenu = ({
    children,
    open,
    anchorRef,
    sx,
    anchorGap = 2,
    slotProps,
    onClose,
    keepPageInteractive = true,
    zIndex,
    ...menuProps
}: PropsWithChildren<IContextMenuProps>) => {
    const theme = useTheme();
    const defaultZIndex = zIndex ?? theme.customZIndex?.contentMenu;
    const paperRef = useNonBlockingMenu({ enabled: keepPageInteractive, open, anchorRef, onClose });
    const popoverActionRef = useRef<PopoverActions | null>(null);

    useEffect(() => {
        if (!open) {
            return;
        }
        const updatePosition = () => popoverActionRef.current?.updatePosition();
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);
        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, [open]);

    return (
        <React.StrictMode>
            <Menu
                action={popoverActionRef}
                open={open}
                anchorEl={anchorRef.current}
                sx={getMergedSx(sx, anchorGap, defaultZIndex)}
                disableScrollLock={true}
                disableEnforceFocus={keepPageInteractive}
                disableRestoreFocus={keepPageInteractive}
                onClose={onClose}
                {...menuProps}
                slotProps={keepPageInteractive ? getNonBlockingSlotProps(slotProps, paperRef) : getDefaultSlotProps(slotProps)}
            >
                {children}
            </Menu>
        </React.StrictMode>
    );
};
